import { BaseEffect } from '../BaseEffect.js';

/**
 * Motion blur effect implementation
 * Creates a directional motion blur effect with configurable intensity and samples
 */
export class MotionBlurEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            intensity: 0.5,        // Strength of the motion blur (0-1)
            angle: 0.0,           // Angle of motion blur in degrees (0-360)
            samples: 16,          // Number of samples (8-32)
            exposure: 1.0,        // Exposure adjustment for the blur
            chromatic: 0.2,       // Chromatic aberration amount
            falloff: 1.0,         // Blur falloff curve
            animate: true,        // Whether to animate the effect
            animationSpeed: 1.0   // Speed of automatic angle rotation
        };

        super('motion-blur', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_intensity;
            uniform float u_angle;
            uniform float u_samples;
            uniform float u_exposure;
            uniform float u_chromatic;
            uniform float u_falloff;
            uniform float u_animationSpeed;
            
            varying vec2 v_texcoord;

            const float PI = 3.14159265359;
            const float TAU = PI * 2.0;

            // Helper function to get sample offset
            vec2 getOffset(float angle, float i, float total) {
                float progress = i / total;
                float radians = angle * PI / 180.0;
                float falloff = pow(progress, u_falloff);
                
                return vec2(
                    cos(radians) * progress * u_intensity * 0.1,
                    sin(radians) * progress * u_intensity * 0.1
                ) * falloff;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                // Calculate effective angle with optional animation
                float effectiveAngle = u_angle;
                if (u_animationSpeed > 0.0) {
                    effectiveAngle += u_time * u_animationSpeed * 30.0;
                }
                
                // Initialize accumulation variables
                vec4 color = vec4(0.0);
                float totalWeight = 0.0;
                
                // Sample loop with chromatic separation
                for (float i = 0.0; i < 32.0; i++) {
                    if (i >= u_samples) break;
                    
                    float weight = 1.0 - (i / u_samples);
                    weight = pow(weight, u_falloff);
                    
                    // Base offset for this sample
                    vec2 offset = getOffset(effectiveAngle, i, u_samples);
                    
                    // Apply chromatic separation
                    vec2 redOffset = offset * (1.0 + u_chromatic * 0.02);
                    vec2 blueOffset = offset * (1.0 - u_chromatic * 0.02);
                    
                    // Sample with chromatic aberration
                    vec4 sample;
                    sample.r = texture2D(u_image, uv + redOffset).r;
                    sample.g = texture2D(u_image, uv + offset).g;
                    sample.b = texture2D(u_image, uv + blueOffset).b;
                    sample.a = texture2D(u_image, uv + offset).a;
                    
                    // Accumulate weighted sample
                    color += sample * weight;
                    totalWeight += weight;
                }
                
                // Normalize and apply exposure
                color /= totalWeight;
                color.rgb *= u_exposure;
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_intensity', { type: '1f' });
        this.addUniform('u_angle', { type: '1f' });
        this.addUniform('u_samples', { type: '1f' });
        this.addUniform('u_exposure', { type: '1f' });
        this.addUniform('u_chromatic', { type: '1f' });
        this.addUniform('u_falloff', { type: '1f' });
        this.addUniform('u_animationSpeed', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_intensity: { value: this.params.intensity, type: '1f' },
            u_angle: { value: this.params.angle, type: '1f' },
            u_samples: { value: this.params.samples, type: '1f' },
            u_exposure: { value: this.params.exposure, type: '1f' },
            u_chromatic: { value: this.params.chromatic, type: '1f' },
            u_falloff: { value: this.params.falloff, type: '1f' },
            u_animationSpeed: { value: this.params.animate ? this.params.animationSpeed : 0.0, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            intensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Blur Intensity'
            },
            angle: {
                min: 0,
                max: 360,
                step: 1,
                label: 'Blur Angle'
            },
            samples: {
                min: 8,
                max: 32,
                step: 1,
                label: 'Sample Count'
            },
            exposure: {
                min: 0.5,
                max: 2.0,
                step: 0.1,
                label: 'Exposure'
            },
            chromatic: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Color Separation'
            },
            falloff: {
                min: 0.5,
                max: 2.0,
                step: 0.1,
                label: 'Blur Falloff'
            },
            animationSpeed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Rotation Speed'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 